// @flow
import sortBy from 'lodash/fp/sortBy';
import reverse from 'lodash/fp/reverse';

import type { PriceGroupToMonetaryAmountMap } from 'modules/monetary-amount';

/**
 * These are sorting helpers.
 *
 * @module modules/sorting
 */

/**
 * Anything we're willing to push into lodash's `sortBy` as the result of an
 * iteratee.
 *
 * Really lodash will take `any` value in `sortBy`, but we'll try and be a bit
 * more discerning for now.
 *
 * @module modules/sorting
 */
export type SortByable = number | string | Date;

/**
 * Pass one of these into a sorter if you want to derive the value to sort your
 * collection on.
 *
 * This is used as a lodash iteratee in `sortBy`.
 *
 * ```
 *   byPosition(pageProducts, false, pp => pp.product.position);
 * ```
 *
 * @module modules/sorting
 */
export type FieldFinder = <P>(obj: any, otherProps?: P) => SortByable;

// Default sorters defined in this file have default field finders for
// convenience. `byPosition` grabs `obj.position` by default, etc.
// type DefaultFieldFinder<O, P> = (obj: O, props: P) => SortByable;

// A sorter takes a collection, whether or not the sort is descending, and a
// FieldFinder which overrides the default.
// type Sorter = <S, P>(
//  arr: S[],
//  descending?: boolean,
//  fieldFinder?: FieldFinder,
//  finderProps?: P,
// ) => S[];

// These interfaces are only used for DefaultFieldFinders. They tell them that
// the parameters to the DefaultFieldFinders will at least be objects with these
// single fields (otherwise the default behavior won't work).

export interface SortableByAvailableOn {
  +available_on: string | null;
}

export interface SortableByPosition {
  +position: number;
}

export interface SortableByPrice {
  prices: PriceGroupToMonetaryAmountMap;
}

export interface SortableBySize {
  +width: number;
  +height: number;
}

// These are the DefaultFieldFinders.

// Now the actual sorters

// export const byPosition: Sorter = makeSorter(positionFieldFinder);
// export const byAvailableOn: Sorter = makeSorter(availableOnFieldFinder);
// export const byPrice: Sorter = makeSorter(priceFieldFinder);

// eslint-disable-next-line arrow-parens
export const sortByDirection = <S>(
  arr: $ReadOnlyArray<S>,
  descending: boolean = false,
  iteratee: (S) => SortByable,
): $ReadOnlyArray<S> => {
  const iteratees: ((S) => SortByable)[] = [iteratee];
  const sorted = sortBy<S>(iteratees, arr);
  return descending ? reverse<S>(sorted) : sorted;
};

// eslint-disable-next-line arrow-parens
export const bySize = <T: SortableBySize>(
  arr: $ReadOnlyArray<T>,
): $ReadOnlyArray<T> => {
  const iteratees: ((T) => number)[] = [(item: T) => item.width * item.height];
  return sortBy<T>(iteratees, arr);
};
